import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export const THEMES = [
  { k: 'morn',      label: 'HARVEST MOON', dot: '#f3c96b' },
  { k: 'hearth',    label: 'NIGHT HEARTH', dot: '#1a0f08' },
  { k: 'dusk',      label: 'AUTUMN DUSK',  dot: '#8a2e1f' },
  { k: 'pumpkin',   label: 'PUMPKIN',      dot: '#d9622c' },
  { k: 'forest',    label: 'FOREST NIGHT', dot: '#3a4a1a' },
  { k: 'parchment', label: 'PARCHMENT',    dot: '#e8d29a' },
] as const;

export type ThemeKey = (typeof THEMES)[number]['k'];
const KEY = 'cosy-theme';
const DEFAULT_THEME: ThemeKey = 'morn';

interface Ctx {
  theme: ThemeKey;
  setTheme: (k: ThemeKey) => void;
}

const ThemeCtx = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeKey>(() => {
    // Validate against the current key list — old installs may have
    // 'map' or 'nightmap' stored, which we no longer ship.
    const stored = localStorage.getItem(KEY);
    return THEMES.some(t => t.k === stored) ? (stored as ThemeKey) : DEFAULT_THEME;
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem(KEY, theme);
  }, [theme]);

  const setTheme = useCallback((k: ThemeKey) => setThemeState(k), []);
  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): Ctx {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error('useTheme must be used inside ThemeProvider');
  return v;
}
